import { Link } from "react-router-dom";
import { COMPANY_INFO, SERVICES_LIST } from "../data/siteData";

export default function Footer() {
  return (
    <footer className="bg-[#1D1F21] text-stone-300 border-t border-zinc-800 pt-16 pb-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-12">
        
        {/* 4-Column Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 items-start">
          
          {/* COLUMN 1: Logo, Availability, CTA, Hours, Email, Social */}
          <div className="space-y-6">
            {/* Logo */}
            <Link to="/" className="inline-block focus:outline-none bg-white p-3 rounded-md">
              <img
                src={COMPANY_INFO.logoUrl}
                alt={COMPANY_INFO.name}
                className="h-12 sm:h-14 w-auto object-contain"
              />
            </Link>

            {/* Operating Hours */}
            <div className="space-y-1.5 text-xs">
              <h4 className="text-white font-extrabold uppercase tracking-wider text-xs mb-2">Åbningstider</h4>
              <p className="flex justify-between border-b border-zinc-800/80 pb-1">
                <span className="text-stone-400">Mandag – Torsdag:</span>
                <span className="font-bold text-[#E07A38]">07:00 – 16:00</span>
              </p>
              <p className="flex justify-between border-b border-zinc-800/80 pb-1">
                <span className="text-stone-400">Fredag:</span>
                <span className="font-bold text-[#E07A38]">07:00 – 15:00</span>
              </p>
              <p className="flex justify-between pb-1">
                <span className="text-stone-400">Lørdag – Søndag:</span>
                <span className="font-bold text-[#E07A38]">Lukket</span>
              </p>
            </div>

            {/* Email Address with Icon */}
            <div className="pt-1">
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="inline-flex items-center gap-2 text-xs text-stone-300 hover:text-[#E07A38] transition-colors font-medium"
              >
                <i className="fa-solid fa-envelope text-[#E07A38]" aria-hidden="true" />
                <span>{COMPANY_INFO.email}</span>
              </a>
            </div>

            {/* Social Media Icons */}
            <div className="flex items-center gap-3 pt-2">
              <a
                href={COMPANY_INFO.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-zinc-800 hover:bg-[#E07A38] text-stone-300 hover:text-white flex items-center justify-center transition-all text-sm"
                aria-label="Følg os på Instagram"
              >
                <i className="fa-brands fa-instagram" aria-hidden="true" />
              </a>
              <a
                href="https://www.houzz.dk/eksperter/snedkere-og-toemrere/kaerby-toemrer-og-snedkerforr-pfvwdk-pf~152842863"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-80 hover:opacity-100 transition-opacity"
                aria-label="Houzz profil"
              >
                <img src={COMPANY_INFO.houzzBadge} alt="Houzz rating" className="h-8 w-auto" />
              </a>
            </div>
          </div>

          {/* COLUMN 2: Location Addresses & CVR */}
          <div className="space-y-6">
            <h3 className="text-white font-extrabold uppercase tracking-wider text-sm border-b border-zinc-800 pb-2">
              Adresse & Information
            </h3>

            <div className="space-y-4 text-xs sm:text-sm">
              <div className="space-y-1">
                <h4 className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-location-dot text-[#E07A38]" aria-hidden="true" />
                  <span>Adresse & Værksted</span>
                </h4>
                <p className="text-stone-400 pl-5">
                  {COMPANY_INFO.address}<br />
                  {COMPANY_INFO.city}
                </p>
              </div>

              <div className="space-y-1">
                <h4 className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-map-location-dot text-[#E07A38]" aria-hidden="true" />
                  <span>Dækningsområde</span>
                </h4>
                <p className="text-stone-400 pl-5">
                  {COMPANY_INFO.area}
                </p>
              </div>

              <div className="space-y-1">
                <h4 className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-phone text-[#E07A38]" aria-hidden="true" />
                  <span>Direkte Telefon</span>
                </h4>
                <p className="text-stone-400 pl-5">
                  <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="text-white font-extrabold hover:text-[#E07A38] transition-colors">
                    +{COMPANY_INFO.phone}
                  </a>
                </p>
              </div>

              <div className="space-y-1">
                <h4 className="text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-file-invoice text-[#E07A38]" aria-hidden="true" />
                  <span>CVR-nummer</span>
                </h4>
                <p className="text-stone-400 pl-5 font-mono">
                  CVR: {COMPANY_INFO.cvr}
                </p>
              </div>
            </div>
          </div>

          {/* COLUMN 3: Link List - Vores Ydelser */}
          <div className="space-y-6">
            <h3 className="text-white font-extrabold uppercase tracking-wider text-sm border-b border-zinc-800 pb-2">
              Vores Ydelser
            </h3>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              {SERVICES_LIST.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/${service.slug}`}
                    className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group"
                  >
                    <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    <span>{service.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* COLUMN 4: Link List - Navigation */}
          <div className="space-y-6">
            <h3 className="text-white font-extrabold uppercase tracking-wider text-sm border-b border-zinc-800 pb-2">
              Navigation
            </h3>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              <li>
                <Link to="/" className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  <span>Forside</span>
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  <span>Om Kærby Tømrer</span>
                </Link>
              </li>
              <li>
                <Link to="/ydelser" className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  <span>Alle Ydelser</span>
                </Link>
              </li>
              <li>
                <Link to="/referencer" className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  <span>Referencer & Galleri</span>
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group">
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  <span>Indhent Tilbud</span>
                </Link>
              </li>
              <li>
                <a
                  href="https://www.houzz.dk/eksperter/snedkere-og-toemrere/kaerby-toemrer-og-snedkerforr-pfvwdk-pf~152842863"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-400 hover:text-[#E07A38] transition-colors flex items-center gap-2 group"
                >
                  <i className="fa-solid fa-chevron-right text-[10px] text-[#E07A38] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  <span>Houzz Anmeldelser</span>
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Sub-Footer / Copyright Bar */}
        <div className="pt-8 border-t border-zinc-800/80 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-stone-500">
          <p>© {new Date().getFullYear()} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-stone-500">
            <span>CVR: {COMPANY_INFO.cvr}</span>
            <span>•</span>
            <a
              href="https://www.iubenda.com/privacy-policy/52015785"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-[#E07A38] transition-colors"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <span>•</span>
            <a
              href="https://www.iubenda.com/privacy-policy/52015785/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-[#E07A38] transition-colors"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}
